import { TbLock } from "solid-icons/tb";
import { type JSX, Match, onCleanup, onMount, type ParentComponent, Switch } from "solid-js";
import "./mockups.css";

/** `app` draws no bar of its own: the desktop window brings its toolbar. */
type FrameChrome = "app" | "window" | "browser" | "phone";

type Size = { width: number; height: number };

/**
 * A device screen laid out at a fixed design size and scaled to fit its
 * container, so a mockup composes like a picture at any width. Below the `sm`
 * breakpoint the screen switches to its `narrow` size, and content built with
 * container queries reflows into a phone-width layout instead of shrinking the
 * desktop one. `title` is the window title, or the URL in a browser.
 */
export const ScaledFrame: ParentComponent<
  Size & { chrome: FrameChrome; narrow?: Size; title?: JSX.Element; class?: string }
> = (props) => {
  let frame: HTMLDivElement | undefined;

  onMount(() => {
    if (!frame) return;
    const box = frame;
    const observer = new ResizeObserver(() => {
      const designWidth = Number.parseFloat(getComputedStyle(box).getPropertyValue("--w"));
      if (designWidth > 0) box.style.setProperty("--scale", String(box.clientWidth / designWidth));
    });
    observer.observe(box);
    onCleanup(() => observer.disconnect());
  });

  return (
    <div
      ref={frame}
      class={`mk-frame border border-primary-border bg-tile text-primary-text shadow-2xl shadow-black/10 ${
        props.chrome === "phone" ? "rounded-[2.75rem] border-[6px] border-mock-bezel" : "rounded-xl"
      } ${props.class ?? ""}`}
      style={{
        "--dw": String(props.width),
        "--dh": String(props.height),
        "--nw": props.narrow ? String(props.narrow.width) : undefined,
        "--nh": props.narrow ? String(props.narrow.height) : undefined,
      }}
    >
      <div class="mk-canvas @container flex flex-col">
        <Switch>
          <Match when={props.chrome === "window"}>
            <div class="relative flex h-10 shrink-0 items-center gap-2 border-b border-primary-border bg-panel px-4">
              <TrafficLights />
              <span class="absolute inset-x-20 truncate text-center text-[13px] font-medium text-secondary-text">
                {props.title}
              </span>
            </div>
          </Match>
          <Match when={props.chrome === "browser"}>
            <div class="flex h-11 shrink-0 items-center gap-4 border-b border-primary-border bg-panel px-4">
              <TrafficLights />
              <span class="mx-auto flex w-1/2 min-w-0 items-center justify-center gap-1.5 rounded-md bg-inset px-3 py-1 font-mono text-xs text-secondary-text">
                <TbLock class="size-3 shrink-0" aria-hidden="true" />
                <span class="truncate">{props.title}</span>
              </span>
              <span class="w-14" />
            </div>
          </Match>
          <Match when={props.chrome === "phone"}>
            <div class="flex h-11 shrink-0 items-center justify-center">
              <span class="h-6 w-24 rounded-full bg-mock-bezel" />
            </div>
          </Match>
        </Switch>
        <div class="relative min-h-0 flex-1 overflow-hidden">{props.children}</div>
      </div>
    </div>
  );
};

const TrafficLights = () => (
  <span class="flex shrink-0 gap-2">
    <span class="size-3 rounded-full bg-mock-close" />
    <span class="size-3 rounded-full bg-mock-minimize" />
    <span class="size-3 rounded-full bg-mock-zoom" />
  </span>
);
